import { InfiniteScroll, VerticalContainer } from '@american-autonomy/ag-ui';

import SearchInput from 'components/SearchInput';
import SelectItemContainer from 'components/SelectItemContainer';
import Panel from 'components/containers/Panel';
import SkeletonText from 'components/loaders/SkeletonText';
import LabelText from 'components/text/LabelText';

import useData from './hooks/useData';
import useTranslation from './hooks/useTranslation';

import { DEBOUNCE_DEFAULT_TIME, TEST_ID } from './constants';
import type { FunctionComponent, Props, RenderedSection } from './types';

import styles from './styles.module.scss';

const SelectPanel: FunctionComponent<Props> = ({
    children,
    className = '',
    debounceTime = DEBOUNCE_DEFAULT_TIME,
    getSectionKey,
    hasNextPage,
    isLoading,
    isSearchable = true,
    items,
    label,
    labelText,
    noItemsFoundText,
    onFilter,
    onNextPage,
    sections,
    testId = TEST_ID
}) => {
    const { NO_ITEMS_FOUND } = useTranslation();

    const { displayItems, handleSearchChange, hasNoItems, isListLoading } = useData({
        isLoading,
        items
    });

    const fetchNext = async () => {
        if (!hasNextPage || !onNextPage) {
            return;
        }

        await onNextPage();
    };

    const isSectioned = !!sections && !!getSectionKey;

    const renderedSections: RenderedSection[] =
        isSectioned && sections
            ? sections
                  .map(section => ({
                      section,
                      sectionItems: displayItems.filter(
                          item => getSectionKey?.(item) === section.key
                      )
                  }))
                  .filter(({ sectionItems }) => sectionItems.length > 0)
            : [];

    const renderSection = ({ section, sectionItems }: RenderedSection, index: number) => {
        const spacingClass = index > 0 ? styles.sectionSpacing : '';

        return (
            <VerticalContainer className={`${styles.section} ${spacingClass}`} key={section.key}>
                <LabelText className={styles.labelText} text={section.labelText} />

                {sectionItems.map(children)}
            </VerticalContainer>
        );
    };

    return (
        <Panel className={`${styles.selectPanel} ${className}`} testId={testId}>
            {isSearchable && (
                <SearchInput
                    className={styles.searchInput}
                    debounceTime={debounceTime}
                    isLoading={isLoading}
                    items={items}
                    onFilter={onFilter}
                    onFilterResults={handleSearchChange}
                    placeholder={label}
                />
            )}

            <SelectItemContainer>
                {isListLoading && hasNoItems && <SkeletonText gap='lg' lines={8} />}

                {hasNoItems && !isListLoading && (
                    <LabelText
                        className={styles.labelText}
                        text={noItemsFoundText || NO_ITEMS_FOUND}
                    />
                )}

                {!hasNoItems && (
                    <>
                        {!isSectioned && (
                            <LabelText className={styles.labelText} text={labelText} />
                        )}

                        <InfiniteScroll
                            className={styles.infiniteScroll}
                            fetchNextPage={fetchNext}
                            hasNextPage={!!hasNextPage}
                            loader={hasNextPage ? <SkeletonText gap='lg' lines={8} /> : null}>
                            {isSectioned
                                ? renderedSections.map(renderSection)
                                : displayItems.map(children)}
                        </InfiniteScroll>
                    </>
                )}
            </SelectItemContainer>
        </Panel>
    );
};

export default SelectPanel;
